import type { JSX } from 'react';
import { FormProvider, type FieldValues } from 'react-hook-form';
import type { CompanyInput } from '@/data/repos/companiesRepo';
import { useDataProvider } from '@/data/provider';
import { queryKeys } from '@/lib/query-keys';
import {
  companyFormSchema,
  companyFormFields,
  type CompanyFormValues,
} from '../../components/companyFormSchema';
import { useUnifiedForm } from '../../../../hooks/useUnifiedForm';
import { useModalMutation } from '../../../../hooks/useModalMutation';
import {
  EntityDialog,
  EntityDialogFooter,
  EntityFormFields,
  FormErrorBanner,
  FormErrorSummary,
  useEntityDialogForm,
  useEntityDialogSubmit,
} from '@/components/blocks/forms';
import { Button, DialogClose } from '@/components/ui';
import { scrollToFirstError } from '@/components/blocks/forms/formErrors';
import { CompanyLogoField } from '../../components/CompanyLogoField';

interface NewCompanyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (companyId: string) => void;
}

export function NewCompanyModal({
  open,
  onOpenChange,
  onCreated,
}: NewCompanyModalProps): JSX.Element {
  const { companies } = useDataProvider();
  const { fields } = useUnifiedForm({
    schema: companyFormSchema,
    fields: companyFormFields,
    fieldOrder: [
      'name',
      'code',
      'vat',
      'sedeLegaleAddress',
      'sedeLegaleCap',
      'sedeLegaleCity',
      'sedeLegaleProvince',
      'notes',
    ],
    fieldOverrides: {
      notes: { colSpan: 2 },
    },
  });

  const createCompany = useModalMutation<CompanyInput, string>({
    mutationFn: (input) => companies.saveCompany(input),
    successMessage: 'Azienda creata con successo',
    errorMessage: "Impossibile salvare l'azienda",
    invalidate: [queryKeys.companies.all],
    onSuccess: (companyId) => {
      onCreated(companyId);
    },
    onError: () => true,
  });

  const { form, formId, errorCount, rootError, handleInvalidSubmit, setRootError } =
    useEntityDialogForm({
      open,
      schema: companyFormSchema,
      fields,
      data: null,
    });

  const handleSave = useEntityDialogSubmit({
    form,
    onSubmit: async (values: FieldValues) => {
      const parsed = values as CompanyFormValues;
      const logoUrl =
        typeof parsed.logoUrl === 'string' && parsed.logoUrl.trim().length
          ? parsed.logoUrl.trim()
          : undefined;
      const payload: CompanyInput = {
        name: parsed.name,
        code: parsed.code,
        vat: parsed.vat,
        sedeLegaleAddress: parsed.sedeLegaleAddress,
        sedeLegaleCap: parsed.sedeLegaleCap,
        sedeLegaleCity: parsed.sedeLegaleCity,
        sedeLegaleProvince: parsed.sedeLegaleProvince,
        sedeLegale: undefined,
        notes: parsed.notes,
        logoUrl,
      };
      await createCompany.mutateAsync(payload);
    },
    setRootError,
  });

  const isSaving = createCompany.isPending;
  const isDirty = form.formState.isDirty;

  const sections = [
    {
      title: 'Informazioni generali',
      fields: [
        'name',
        'code',
        'vat',
        'sedeLegaleAddress',
        'sedeLegaleCap',
        'sedeLegaleCity',
        'sedeLegaleProvince',
      ],
    },
    { title: 'Note', fields: ['notes'] },
  ];

  return (
    <EntityDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Nuova azienda"
      size="sm"
      isDirty={isDirty}
      isSaving={isSaving}
      formId={formId}
      banner={rootError ? <FormErrorBanner message={rootError} /> : null}
      summary={
        errorCount > 0 ? (
          <FormErrorSummary
            count={errorCount}
            onJumpToFirstError={() => scrollToFirstError(form.formState.errors)}
          />
        ) : null
      }
      footer={
        <EntityDialogFooter
          secondary={
            <DialogClose asChild>
              <Button variant="ghost" disabled={isSaving}>
                Chiudi
              </Button>
            </DialogClose>
          }
          primary={
            <Button type="submit" form={formId} disabled={!isDirty || isSaving}>
              {isSaving ? 'Salvataggio…' : 'Crea azienda'}
            </Button>
          }
        />
      }
    >
      <FormProvider {...form}>
        <form
          id={formId}
          onSubmit={form.handleSubmit(handleSave, handleInvalidSubmit)}
          noValidate
          className="space-y-4"
        >
          <CompanyLogoField disabled={isSaving} />
          <EntityFormFields fields={fields} sections={sections} disabled={isSaving} />
        </form>
      </FormProvider>
    </EntityDialog>
  );
}
